import { BackendHttpService } from "./backendHttpService";

export interface UserProfileData {
  displayName: string;
  department?: string;
  companyName?: string;
  officeLocation?: string;
  mail?: string;
  userPrincipalName?: string;
  jobTitle?: string;
  businessPhones?: string[];
  mobilePhone?: string;
  id: string;
}

export interface GraphUserPhoto {
  base64String: string;
  contentType: string;
}

export interface GraphUserManager {
  id: string;
  displayName: string;
  mail: string;
  userPrincipalName: string;
}

export interface GraphUserDirectReports {
  value: UserProfileData[];
}

export interface GraphUserMemberships {
  value: GraphGroup[];
}

export interface GraphGroup {
  id: string;
  displayName: string;
  description: string;
}

export interface GraphCompleteUserData {
  profile: UserProfileData;
  photo: GraphUserPhoto;
  manager: GraphUserManager;
  directReports: GraphUserDirectReports;
  memberships: GraphUserMemberships;
}

export class GraphService extends BackendHttpService {
  constructor() {
    super("Graph");
    console.log(
      "[GraphService] Backend-based GraphService loaded - version 2025.07.21"
    );
  }

  /**
   * Get current user profile from backend Graph API
   */
  async getCurrentUserProfile(): Promise<UserProfileData> {
    try {
      const response = await this.get<UserProfileData>("/profile");
      return response;
    } catch (error) {
      console.error("[GraphService] Error fetching profile:", error);
      throw error;
    }
  }

  /**
   * Get selected user information from backend Graph API
   * This now returns the same data as getCurrentUserProfile since backend handles field selection
   */
  async getSelectedUserInfo(): Promise<UserProfileData> {
    return this.getCurrentUserProfile();
  }

  /**
   * Get ALL available user data from backend Graph API
   */
  async getAllUserData(): Promise<GraphCompleteUserData> {
    try {
      const response = await this.get<GraphCompleteUserData>("/complete");
      return response;
    } catch (error) {
      console.error("[GraphService] Error fetching complete user data:", error);
      throw error;
    }
  }

  /**
   * Get additional user information from backend Graph API
   */
  async getAdditionalUserInfo(): Promise<{
    manager?: GraphUserManager;
    directReports?: GraphUserDirectReports;
    memberOf?: GraphUserMemberships;
    error?: string;
  }> {
    try {
      const completeData = await this.getAllUserData();
      return {
        manager: completeData.manager,
        directReports: completeData.directReports,
        memberOf: completeData.memberships,
      };
    } catch (error) {
      console.error(
        "[GraphService] Error fetching additional user info:",
        error
      );
      return { error: "Failed to fetch additional user data" };
    }
  }

  /**
   * Get current user photo from backend Graph API
   */
  async getCurrentUserPhoto(): Promise<string | null> {
    try {
      const response = await this.get<GraphUserPhoto>("/photo");
      if (response && response.base64String) {
        return `data:${response.contentType};base64,${response.base64String}`;
      }
      return null;
    } catch (error) {
      console.error("[GraphService] Error fetching profile photo:", error);
      return null;
    }
  }

  /**
   * Get current user photo as base64 string from backend Graph API
   */
  async getCurrentUserPhotoAsBase64(): Promise<string | null> {
    try {
      const response = await this.get<GraphUserPhoto>("/photo");
      return response?.base64String || null;
    } catch (error) {
      console.error(
        "[GraphService] Error fetching profile photo as base64:",
        error
      );
      return null;
    }
  }
}
